/**
 * PublicSessionLinkPage.jsx : page du lien de séance, sans compte (D243, D247).
 *
 * Adresse `/seance-partagee#<jeton>` : le jeton reste dans le fragment (le
 * rechargement fonctionne), il est gardé en mémoire et envoyé en en-tête aux
 * routes publiques. Montée avant <App /> (index.js) : aucun appel
 * authentifié, aucun menu de l'application.
 *
 * Contenu : blocs de la fiche session en lecture seule, nourris par une seule
 * analyse (adaptateur + buildPeriodAnalysis), comme le PDF. Absents faute de
 * données dans le payload : carte, météo, annotations, photos, notes
 * vétérinaires, modification, suppression, comparaison, changement de cheval,
 * partage, barre de période (la portée est celle du lien).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getTheme, TYPE } from '../../constants/theme';
import SessionStats from '../SessionStats';
import SessionSummary from '../SessionSummary';
import SessionCharts from '../SessionCharts';
import SessionCombinedChart from '../SessionCombinedChart';
import PeriodBanner from '../period/PeriodBanner';
import PDFExport from '../../export/PDFExport';
import { usePublicSessionLink } from './usePublicSessionLink';
import { readTokenFromHash } from './publicSessionLinkService';
import { formatLinkDate } from './linkText';
import { PublicLinkError, PublicLinkHeader, PublicTopBar } from './_publicBlocks';

const NO_ANNOTATIONS = [];

/** Page non indexée : balise posée tant que la page est montée (l'en-tête X-Robots-Tag la double côté serveur). */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);
}

/** Jeton du fragment, relu si le fragment change (lien collé dans le même onglet). */
function useHashToken() {
  const [token, setToken] = useState(() => readTokenFromHash(window.location.hash));
  useEffect(() => {
    const onHash = () => setToken(readTokenFromHash(window.location.hash));
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  return token;
}

export default function PublicSessionLinkPage() {
  const { t } = useTranslation();
  const theme = getTheme(false);
  useNoIndex();
  const token = useHashToken();
  const link = usePublicSessionLink(token);
  const { analysis, session, payload, templates, readings, status } = link;

  // Sources du PDF : ni annotations ni photos, modèles lus sous le jeton, mention du lien.
  const pdfSources = useMemo(() => (payload ? {
    loadAnnotations: null,
    loadFrTemplates: async () => templates,
    photos: false,
    footerMention: t('sessionLink.public.pdfMention', { date: formatLinkDate(payload.expiresAt) }),
  } : null), [payload, templates, t]);

  // Une période illisible ne s'affiche jamais comme la séance entière.
  const errorCode = status === 'error' ? link.errorCode : (analysis?.error ? 'SERVER_ERROR' : null);

  const content = () => {
    if (status === 'loading') {
      return <p role="status" style={{ ...TYPE.body, textAlign: 'center', padding: 40, color: theme.textSecondary }}>{t('sessionLink.public.loading')}</p>;
    }
    if (errorCode) return <PublicLinkError code={errorCode} theme={theme} />;
    const windowKey = analysis.active ? `${analysis.window.start}-${analysis.window.end}` : 'session';
    return (
      <>
        <PublicLinkHeader session={session} analysis={analysis} expiresAt={payload.expiresAt} theme={theme}
          actions={<PDFExport session={session} sessionId={null} readings={readings}
            period={analysis.active ? analysis : null} sources={pdfSources} />} />
        <PeriodBanner analysis={analysis} />
        <SessionCombinedChart readings={analysis.readings} windowKey={windowKey} onQuickAnnotate={null} onAnalyzeZoom={null} />
        <SessionStats session={analysis.session} readings={analysis.readings} analysis={analysis} />
        <SessionSummary session={analysis.session} readings={analysis.readings}
          scope={analysis.active ? 'period' : 'session'} templates={templates} />
        <SessionCharts readings={analysis.readings} annotations={NO_ANNOTATIONS} windowKey={windowKey}
          onQuickAnnotate={null} onAnalyzeZoom={null} />
        <p style={{ ...TYPE.body, fontSize: 12, marginTop: 24, color: theme.textSecondary }}>{t('sessionLink.public.notShared')}</p>
      </>
    );
  };

  return (
    <div data-testid="public-session-link-page" style={{ minHeight: '100vh', background: theme.bg }}>
      <PublicTopBar />
      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '20px' }}>{content()}</main>
    </div>
  );
}
